/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */

"use client";
import React, { useMemo, useState } from "react";
import { Input } from "@heroui/input";
import Image from "next/image";
import { Button } from "@heroui/button";
import { Accordion, AccordionItem } from "@heroui/accordion";
import "@/styles/rich-text-content.css";
import { motion } from "motion/react";
import Fuse from "fuse.js";

interface FAQItem {
  id: number;
  meta: {
    type: "static_pages.FAQItem";
  };
  question: string;
  answer: string;
  category: {
    name: string;
    priority: number;
  }[];
}

interface FAQElementProps {
  faqList: FAQItem[];
}

function organizeFaqByCategory(faqList: FAQItem[]) {
  // Group by category name with safe fallbacks
  const categorized = faqList.reduce((acc: Record<string, FAQItem[]>, item) => {
    const firstCat = item?.category?.[0];
    const categoryName = firstCat?.name || "General";

    if (!acc[categoryName]) acc[categoryName] = [];
    acc[categoryName].push(item);

    return acc;
  }, {});

  // Return sorted buckets using the first item's category priority as the group's priority
  return Object.keys(categorized)
    .map((categoryName) => ({
      name: categoryName,
      items: categorized[categoryName],
      priority:
        categorized[categoryName][0]?.category?.[0]?.priority ??
        Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.priority - b.priority);
}

// Strip HTML tags/styles/scripts to plain text for search indexing
const stripHtml = (html: string) =>
  (html || "")
    .replace(/<style[^>]*>.*?<\/style>/gis, "")
    .replace(/<script[^>]*>.*?<\/script>/gis, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const FAQElement: React.FC<FAQElementProps> = ({ faqList }) => {
  const initialBuckets = useMemo(
    () => organizeFaqByCategory(faqList),
    [faqList],
  );

  const [sortedFaqList, setSortedFaqList] = useState(initialBuckets);
  const [selectedSection, setSelectedSection] = useState<string | null>(
    initialBuckets[0]?.name ?? null,
  );
  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(
    initialBuckets[0]?.items?.[0]?.id ?? null,
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Build a parallel list with a plain-text answer for search
  const searchableFaqList = useMemo(
    () =>
      faqList.map((item) => ({
        ...item,
        _plainAnswer: stripHtml(item.answer),
      })),
    [faqList],
  );

  // Memoized Fuse instance
  const fuse = useMemo(
    () =>
      new Fuse(searchableFaqList, {
        keys: [
          { name: "question", weight: 0.6 },
          { name: "_plainAnswer", weight: 0.4 },
        ],
        includeScore: true,
        threshold: 0.3,
        ignoreLocation: true,
        minMatchCharLength: 2,
      }),
    [searchableFaqList],
  );

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const trimmed = query.trim();

    // Reset to full list for empty/short queries
    if (!trimmed || trimmed.length < 2) {
      const reset = organizeFaqByCategory(faqList);

      setSortedFaqList(reset);
      if (reset.length) {
        setSelectedSection(reset[0].name);
        setSelectedQuestion(reset[0].items?.[0]?.id ?? null);
      } else {
        setSelectedSection(null);
        setSelectedQuestion(null);
      }

      return;
    }

    // Search
    const results = fuse.search(trimmed).map((r) => r.item as FAQItem);

    if (results.length === 0) {
      setSortedFaqList([]);
      setSelectedSection(null);
      setSelectedQuestion(null);

      return;
    }

    const filtered = organizeFaqByCategory(results);

    setSortedFaqList(filtered);
    setSelectedSection(filtered[0].name);
    setSelectedQuestion(filtered[0].items?.[0]?.id ?? null);
  };

  return (
    <section className="flex flex-col items-center justify-center px-4 md:px-14 xl:px-[100px] max-w-[1440px] mx-auto w-f">
      <div className="w-full">
        <div className="flex flex-col items-center justify-center w-full">
          <Input
            className="w-full max-w-[900px]"
            classNames={{
              inputWrapper: "bg-[#FCFCFC] px-6 py-2 h-[45px] md:h-14",
            }}
            endContent={
              <Image
                alt="magnifier"
                className="w-5 h-5 md:w-6 md:h-6 object-contain"
                height={24}
                src="/icons/magnifier.svg"
                width={24}
              />
            }
            placeholder="Type Your Question Here"
            radius="full"
            value={searchQuery}
            variant="bordered"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        {sortedFaqList && sortedFaqList.length ? (
          <React.Fragment>
            {/* Desktop / Tablet */}
            <div className="hidden md:flex flex-row items-start justify-start w-full gap-20 mt-7 md:mt-10">
              <div className="flex flex-col items-start justify-start gap-3">
                {sortedFaqList.map((item, index) => (
                  <Button
                    key={`${item.name}-${index}`}
                    className={`text-left text-xl w-full justify-start ${
                      item.name === selectedSection
                        ? "bg-[#FBCECE]/50 text-ax-secondary [&:hover]:bg-[#FBCECE]/30 [&:hover]:text-ax-secondary/80"
                        : ""
                    }`}
                    radius="sm"
                    variant="light"
                    onPress={() => {
                      setSelectedSection(item.name);
                      setSelectedQuestion(item.items?.[0]?.id ?? null);
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
              </div>

              <div className="flex flex-col items-start justify-start w-full gap-10">
                {sortedFaqList.map((item, index) => (
                  <motion.div
                    key={`${item.name}-${index}`}
                    animate={{
                      opacity: item.name === selectedSection ? 1 : 0.5,
                      y: item.name === selectedSection ? 0 : 50,
                    }}
                    className={`flex-col items-start justify-start w-full gap-2 md:gap-4 ${
                      item.name === selectedSection ? "flex" : "hidden"
                    }`}
                    exit={{ opacity: 0.5, y: -50 }}
                    initial={{ opacity: 0.5, y: 50 }}
                    transition={{ duration: 0.6 }}
                  >
                    <h3 className="text-xl md:text-3xl font-medium">
                      {item.name}
                    </h3>

                    <Accordion
                      className="w-full"
                      selectedKeys={
                        selectedQuestion ? [String(selectedQuestion)] : []
                      }
                      showDivider={false}
                      onSelectionChange={(key: any) =>
                        setSelectedQuestion(
                          key.currentKey ? Number(key.currentKey) : null,
                        )
                      }
                    >
                      {item.items.map((faq) => (
                        <AccordionItem
                          key={faq.id}
                          hideIndicator
                          classNames={{
                            title: `text-xl ${faq.id === selectedQuestion ? "text-ax-secondary" : ""}`,
                            trigger: "py-5 cursor-pointer",
                            base: "border-b-[1px] border-black/10",
                          }}
                          startContent={
                            <p
                              className={`text-xl font-semibold ${
                                faq.id === selectedQuestion
                                  ? "text-ax-secondary"
                                  : ""
                              }`}
                            >
                              {faq.id === selectedQuestion ? "—" : "+"}
                            </p>
                          }
                          title={faq.question}
                        >
                          <div
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                            className="flex flex-col items-start justify-start dynamic-rich-text-content pt-0 md:pt-3 pb-5 md:pb-10"
                          />
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Mobile */}
            <div className="md:hidden flex flex-col items-start justify-start w-full mt-7">
              <Accordion
                className="w-full"
                selectedKeys={selectedSection ? [selectedSection] : []}
                showDivider={false}
                onSelectionChange={(key: any) =>
                  setSelectedSection(key.currentKey ?? null)
                }
              >
                {sortedFaqList.map((item) => (
                  <AccordionItem
                    key={item.name}
                    hideIndicator
                    classNames={{
                      title: `${item.name === selectedSection ? "text-ax-secondary" : ""}`,
                      trigger: `px-5 py-4 cursor-pointer rounded-lg ${
                        item.name === selectedSection ? "bg-[#FBCECE]/50" : ""
                      }`,
                    }}
                    textValue={item.name}
                    title={
                      <div className="flex flex-row items-center justify-between w-full gap-4">
                        <p className="text-xl">{item.name}</p>
                        <p className="text-xl font-semibold text-ax-secondary">
                          {selectedSection === item.name ? "—" : "+"}
                        </p>
                      </div>
                    }
                  >
                    <Accordion
                      className="w-full"
                      selectedKeys={
                        selectedQuestion ? [String(selectedQuestion)] : []
                      }
                      showDivider={false}
                      onSelectionChange={(key: any) =>
                        setSelectedQuestion(
                          key.currentKey ? Number(key.currentKey) : null,
                        )
                      }
                    >
                      {item.items.map((faq) => (
                        <AccordionItem
                          key={faq.id}
                          hideIndicator
                          classNames={{
                            title: `text-xl ${faq.id === selectedQuestion ? "text-ax-secondary" : ""}`,
                            trigger: "py-5 cursor-pointer",
                            base: "border-b-[1px] border-black/10",
                          }}
                          startContent={
                            <p
                              className={`text-xl font-semibold ${
                                faq.id === selectedQuestion
                                  ? "text-ax-secondary"
                                  : ""
                              }`}
                            >
                              {faq.id === selectedQuestion ? "—" : "+"}
                            </p>
                          }
                          title={faq.question}
                        >
                          <div
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                            className="flex flex-col items-start justify-start dynamic-rich-text-content pt-0 md:pt-3 pb-5 md:pb-10"
                          />
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </React.Fragment>
        ) : (
          <div className="flex flex-col items-center justify-center gap-4 mt-10">
            <p className="text-xl text-center">
              There are no FAQs matching your search. <br /> But you can still
              get in touch with us.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQElement;
